import { useMemo } from "react";
import { Column, SkeletonBox, Text } from "@hightouch/htui";
import type { ContextAttachment } from "src/pages/agents/types";

import type { ContextItem } from "src/components/llm/components/compact-chat-input";
import { AdStudioTemplatePreview } from "src/pages/ad-studio/components/ad-studio-template-preview";
import type { TemplateSelection } from "src/pages/ad-studio/components/ad-studio-template-selection";
import { useAdStudioTemplateOptions } from "src/pages/ad-studio/components/use-ad-studio-template-options";
import type { AdStudioTemplateCard } from "src/pages/ad-studio/templates/types";

// Share image-asset resize requests between the pill and its hover preview.
const PILL_RESIZE_DIMENSION = 320;

/**
 * One composer context pill per selected template, in selection order.
 * Ids without a matching template (deleted, or reassigned to another brand)
 * are skipped.
 */
export function buildAdStudioTemplateContextItems(
  templates: AdStudioTemplateCard[],
  ids: string[],
): ContextItem[] {
  return ids.flatMap((id) => {
    const template = templates.find((candidate) => candidate.id === id);
    if (!template) return [];
    return [
      {
        id: template.id,
        label: template.name,
        thumbnail: (
          <AdStudioTemplatePreview
            template={template}
            width="32px"
            height="32px"
            naturalWidth={PILL_RESIZE_DIMENSION}
            naturalHeight={PILL_RESIZE_DIMENSION}
          />
        ),
        popoverPreview: (
          <AdStudioTemplatePreview
            template={template}
            width="200px"
            height="200px"
            naturalWidth={PILL_RESIZE_DIMENSION}
            naturalHeight={PILL_RESIZE_DIMENSION}
          />
        ),
        messageAttachment: buildAdStudioTemplateAttachments(templates, [id])[0],
        referenceAssetId: null,
        // No imageSrc: the zoom gallery only supports bitmap URLs.
      },
    ];
  });
}

export function useAdStudioTemplateContextItems({
  brandProfileId,
  selection,
}: {
  brandProfileId: string | undefined;
  selection: TemplateSelection;
}): ContextItem[] {
  const { templates } = useAdStudioTemplateOptions(brandProfileId);
  return useMemo(
    () => buildAdStudioTemplateContextItems(templates ?? [], selection.ids),
    [templates, selection.ids],
  );
}

/** Historical attachments use all accessible templates, regardless of picker eligibility. */
export function buildAdStudioTemplateAttachments(
  templates: AdStudioTemplateCard[] | undefined,
  ids: string[],
): ContextAttachment[] {
  const templatesById = new Map(
    templates?.map((template) => [template.id, template]),
  );
  return ids.map((id) => {
    const template = templatesById.get(id);
    return {
      id,
      label:
        template?.name ??
        (templates ? "Template unavailable" : "Loading template…"),
      preview: template ? (
        <AdStudioTemplatePreview
          template={template}
          naturalWidth={PILL_RESIZE_DIMENSION}
          naturalHeight={PILL_RESIZE_DIMENSION}
        />
      ) : templates ? (
        <Column height="100%" justifyContent="center" alignItems="center" p={2}>
          <Text size="sm" color="text.secondary">
            Template unavailable
          </Text>
        </Column>
      ) : (
        <SkeletonBox width="100%" height="100%" />
      ),
    };
  });
}
